import { AllocatableLease } from './lease-distribution-selector.component'

/** Por debajo de esto una letra se considera saldada. Espeja la tolerancia del motor. */
const TOLERANCE = 0.01

/**
 * Convierte los arrendamientos que devuelve la API en lo que el selector necesita para repartir.
 *
 * **La capacidad se deriva de las LETRAS, no de los campos agregados del arrendamiento.**
 * `periodsOverdue` y `amountToUpdate` los escribe únicamente el devengo nocturno, y con la mora apagada
 * ese proceso sale sin tocar nada: pintarlos sería enseñar una cifra congelada el día que alguien
 * encienda el interruptor.
 *
 * **Y la mora no se calcula aquí.** `moraAmount` es lo que el motor dejó MATERIALIZADO en la letra, que
 * es lo que hoy se puede cobrar; lo que la mora vale a la fecha de un pago concreto depende de una
 * función que capitaliza a diario y necesita los movimientos de dos colecciones que ningún endpoint
 * expone. Por eso la pantalla ancla sus cifras a una fecha y deja que la previsualización mande.
 */
export function toAllocatableLeases(leases: any[], moraEnabled: boolean, today: Date = new Date()): AllocatableLease[] {
  return (leases || []).map((lease) => {
    const periods: any[] = lease?.quotation?.amortizationPeriods || []

    //* Elegible por SALDO REAL, no por estatus, igual que `eligiblePeriods` del API: una letra PAID que todavía
    //* debe sí recibe dinero, y una ANTICIPATED también si le queda su accesorio por vencer (C5, 28-sep-2026).
    const eligibles = periods.filter((p) => p && pendingOf(p) > TOLERANCE)

    //! La capacidad es `payment` y NADA MÁS: ese campo YA lleva la mora dentro.
    //!
    //! Los tres sitios que lo escriben coinciden — el motor de cotización lo nace como la renta (aún no
    //! hay mora devengada), el motor de aplicaciones lo reescribe como `periodPending(period, mora)` y
    //! el devengo nocturno igual (`mora-accrual.service.ts`) —, y `periodPending` parte de
    //! `periodTotalAmount`, que suma `moraExigible`. Antes se hacía `payment + mora` y la mora se
    //! contaba DOS VECES. Hoy no se nota porque el interruptor está en pausa y el sumando es cero; el día
    //! que se encienda, cada cifra de esta pantalla se infla.
    const capacity = round2(eligibles.reduce((s, p) => s + pendingOf(p), 0))

    //* La mora que todavía se debe, para el desglose: la exigible MENOS la ya cobrada. Es la misma resta
    //* que hace el devengo (`mora-accrual.service.ts`); sin restar lo cobrado, el desglose decía que se
    //* debe una mora que el cliente ya pagó.
    const moraPending = round2(eligibles.reduce((s, p) => s + moraDebida(p, moraEnabled), 0))
    //* La renta es el resto: lo que se debe menos la parte que es mora. Así los dos suman la capacidad.
    const rentPending = round2(Math.max(0, capacity - moraPending))

    const vencidas = eligibles.filter((p) => p.date && new Date(p.date).getTime() < today.getTime())
    const masAntigua = vencidas.reduce<Date | null>((old, p) => {
      const d = new Date(p.date)
      return !old || d < old ? d : old
    }, null)

    return {
      leaseId: String(lease?._id ?? ''),
      folio: lease?.folio || '—',
      capacity,
      rentPending,
      moraPending,
      openLetters: eligibles.length,
      oldestOverdueDays: masAntigua ? daysBetween(masAntigua, today) : null,
      //* Se cuentan las dos formas de marcarlas: el motor de cotización deja las anticipadas como `PAID`
      //* con todo en cero y solo el de migración escribe `ANTICIPATED`, así que filtrar por el estatus se
      //* pierde las de los contratos originados hoy.
      anticipatedCount: periods.filter((p) => p?.status === 'ANTICIPATED').length || Number(lease?.quotation?.termData?.advancedRents?.number || 0),
    }
  })
}

/** Lo que la letra todavía debe. `payment` ya trae la mora dentro: no se le suma nada. */
function pendingOf(p: any): number {
  return Math.max(0, p.payment || 0)
}

/** Mora exigible: la materializada menos la condonada, y cero si el interruptor está en pausa. */
function moraOf(p: any, moraEnabled: boolean): number {
  if (!moraEnabled) return 0
  return Math.max(0, round2((p.moraAmount || 0) - (p.moraWaived || 0)))
}

/** Mora que todavía se DEBE: la exigible menos la que el cliente ya pagó. */
function moraDebida(p: any, moraEnabled: boolean): number {
  const cobrada = round2((p.appliedAmounts && p.appliedAmounts.moraAmount) || 0)
  return Math.max(0, round2(moraOf(p, moraEnabled) - cobrada))
}

/** Días de CALENDARIO, no instantes: el vencimiento se guarda como día y convertirlo a zona lo retrocede. */
function daysBetween(from: Date, to: Date): number {
  const a = Date.UTC(from.getUTCFullYear(), from.getUTCMonth(), from.getUTCDate())
  const b = Date.UTC(to.getFullYear(), to.getMonth(), to.getDate())
  return Math.max(0, Math.round((b - a) / 86400000))
}

function round2(n: number): number {
  return Math.round((n + Number.EPSILON) * 100) / 100
}
